// src/data/positionPoints.js
// The canonical forks: moments in the modules where the reader commits to a
// position with one tap. Choices are stored on-device and assembled into the
// "Your Philosophy, So Far" page. Lens lines appear only AFTER choosing —
// never lead the witness.
//
// Placement: { module: <order 1-8>, section: <0-based section index> } — the
// reader hits the fork at the end of that section's page.

export const POSITION_POINTS = [
  {
    id: 'truth-or-victory',
    module: 2,
    section: 1,
    title: 'Socrates and the Sophists',
    question: 'The Sophists taught Athens to win arguments (for a fee, whichever side paid). Socrates argued to find out what was true, and treated being refuted as a favour. When winning and truth pull apart, what is arguing actually for?',
    options: [
      { id: 'victory', label: 'Winning. Persuasion is how things get done', lens: 'The Sophists\' answer, and not a foolish one: rhetoric runs parliaments, courtrooms and every pitch you will ever make. The question that follows you out the door: what does it cost you to argue well for what you privately doubt?' },
      { id: 'truth', label: 'Truth, even when I lose', lens: 'Socrates\' answer: a lost argument that corrects you is a gift, and the detector you are building in this module points at your own claims first.' }
    ]
  },
  {
    id: 'gettier-knowledge',
    module: 3,
    section: 0,
    title: 'The Stopped Clock',
    question: 'The station clock says two o\'clock. It is two o\'clock. The clock stopped twelve hours ago. Did you KNOW the time?',
    options: [
      { id: 'knew', label: 'Yes. I believed it, with good reason, and I was right', lens: 'You side with the classical definition and against Gettier, a minority position among philosophers since 1963, though not an undefended one.' },
      { id: 'lucky', label: 'No. I got lucky; the clock was broken', lens: 'You feel the Gettier intuition: knowledge needs a tighter link between why you believe and what makes it true than luck can supply.' }
    ]
  },
  {
    id: 'awake-proof',
    module: 3,
    section: 2,
    title: 'The Dream Test',
    question: 'Right now, can you prove you are awake?',
    options: [
      { id: 'can', label: 'Yes, I can prove it', lens: 'Bold. Descartes would like a word. Every test you could run is one you could also dream you ran.' },
      { id: 'cannot', label: 'No, and it bothers me less than expected', lens: 'You have accepted the demolition and kept walking: calibrated confidence over demanded certainty.' }
    ]
  },
  {
    id: 'vat-matters',
    module: 3,
    section: 2,
    title: 'The Vat Question',
    question: 'If you learnt tomorrow that you were a brain in a vat, would anything about how you should live actually change?',
    options: [
      { id: 'everything', label: 'Everything would change', lens: 'You tie meaning to metaphysical reality: the simulation would rob your commitments of something essential.' },
      { id: 'nothing', label: 'Honestly, nothing would change', lens: 'You side with the pragmatists: a doubt that changes no decision is idle, and your loves would still be yours.' }
    ]
  },
  {
    id: 'trolley-switch',
    module: 4,
    section: 0,
    title: 'The Switch',
    question: 'A runaway trolley will kill five people. You can divert it to a side track where it will kill one. Do you pull the lever?',
    options: [
      { id: 'pull', label: 'Pull it. One death beats five', lens: 'You weighed outcomes. That is consequentialist reasoning, and this module is its home ground.' },
      { id: 'refuse', label: 'I don’t pull it', lens: 'You refused to make yourself the agent of a death: a deontological instinct about doing versus allowing.' }
    ]
  },
  {
    id: 'trolley-footbridge',
    module: 4,
    section: 0,
    title: 'The Footbridge',
    question: 'Same trolley, same five people, but now the only way to stop it is to push a large stranger off a footbridge into its path. Do you push?',
    options: [
      { id: 'push', label: 'Push. The arithmetic hasn’t changed', lens: 'Ruthlessly consistent consequentialism. Most people flinch here; you did not.' },
      { id: 'refuse', label: 'No, that’s different', lens: 'Using a person as a means feels categorically worse than diverting a threat. Kant is nodding.' }
    ]
  },
  {
    id: 'free-will-otherwise',
    module: 5,
    section: 0,
    title: 'Could You Have Done Otherwise?',
    question: 'Think of the last real choice you made today. Could you, in exactly that situation, being exactly who you were, have chosen differently?',
    options: [
      { id: 'yes', label: 'Yes, genuinely', lens: 'The libertarian intuition: something in you escapes the causal chain. Now the module’s job is to ask what that could be.' },
      { id: 'no', label: 'Probably not, in that exact state', lens: 'You lean determinist: the live question becomes whether freedom can mean something other than escaping causation.' },
      { id: 'redefine', label: 'Wrong question. Freedom isn’t about that', lens: 'That is the compatibilist move: freedom as acting from your own reasons, not from nowhere.' }
    ]
  },
  {
    id: 'ship-of-theseus',
    module: 5,
    section: 1,
    title: 'The Ship of Theseus',
    question: 'Every plank replaced over the years, one by one. Is it the same ship?',
    options: [
      { id: 'same', label: 'Same ship', lens: 'Continuity of form and function carries identity for you: gradual change preserves the thing.' },
      { id: 'different', label: 'Different ship', lens: 'You anchor identity in material stuff: swap the matter, lose the thing.' },
      { id: 'no-fact', label: 'There’s no fact of the matter', lens: 'The dissolving move: "same ship" was always a convention, not a discovery. Nagasena parked a chariot here long before you arrived.' }
    ]
  },
  {
    id: 'teleporter',
    module: 5,
    section: 1,
    title: 'The Teleporter',
    question: 'The machine scans you, destroys this body, and builds a perfect copy (same memories, same personality) on Mars. Do you step in?',
    options: [
      { id: 'step-in', label: 'Step in. That’s still me', lens: 'Psychological continuity is what matters to you; the meat is replaceable.' },
      { id: 'refuse', label: 'Absolutely not. That’s a copy', lens: 'You bet identity on this particular body and brain. The Mars arrival is your twin, not you.' }
    ]
  },
  {
    id: 'chinese-room',
    module: 6,
    section: 0,
    title: 'The Room’s Verdict',
    question: 'The man in the room passes every test in Chinese without understanding a word. Does the SYSTEM (man, rulebook and room together) understand Chinese?',
    options: [
      { id: 'understands', label: 'Yes, the system understands', lens: 'The systems reply: understanding lives at the level of the whole, not the parts. Functionalism’s wager.' },
      { id: 'simulates', label: 'No, it only simulates understanding', lens: 'You side with Searle: syntax is not semantics, however fluent. The question is what biology adds.' },
      { id: 'torn', label: 'I’m genuinely torn', lens: 'Honest company: more than forty years of philosophers are torn with you.' }
    ]
  },
  {
    id: 'eternal-recurrence',
    module: 8,
    section: 1,
    title: 'The Demon’s Offer',
    question: 'This life, exactly as you have lived it, again and again forever: every joy, every pain, in the same sequence. Your gut answer?',
    options: [
      { id: 'affirm', label: 'Yes, again', lens: 'Nietzsche’s test passed: something in your life you would sign for eternally.' },
      { id: 'curse', label: 'I’d curse the demon', lens: 'The honest majority answer, and exactly the information Nietzsche wanted you to have about your current life.' }
    ]
  },
  {
    id: 'meaning-source',
    module: 8,
    section: 2,
    title: 'Where Meaning Lives',
    question: 'When your life feels meaningful, is that meaning something you FOUND, or something you MADE?',
    options: [
      { id: 'found', label: 'Found. It was there to discover', lens: 'You stand with the traditions (Rumi among them) that read the longing itself as pointing at something real.' },
      { id: 'made', label: 'Made. Built by my commitments', lens: 'Camus’ side of the ledger: meaning as a thing constructed, boulder by boulder.' }
    ]
  }
];

// Tensions: pairs of answers that pull against each other. Fired tensions are
// the page's most valuable output — a named, honest "worth sitting with".
export const POSITION_TENSIONS = [
  {
    id: 'consequentialist-flinch',
    name: 'The Consequentialist’s Flinch',
    requires: { 'trolley-switch': 'pull', 'trolley-footbridge': 'refuse' },
    insight: 'You pulled the lever but refused the push: the arithmetic was identical, so something other than arithmetic is doing moral work in you. You are in the majority, and the difference between diverting a threat and using a person is exactly where consequentialism and deontology part ways. Module 4 gave both sides their say; your instincts are running both frameworks at once.'
  },
  {
    id: 'continuity-bet',
    name: 'The Continuity Bet',
    requires: { 'ship-of-theseus': 'same', 'teleporter': 'refuse' },
    insight: 'You let the ship survive plank-by-plank replacement, but refused to survive the teleporter, yet both preserve structure and continuity of pattern. Is the difference the gradualness? The destruction moment? Or does your intuition quietly insist that YOU are the matter, not the pattern, in a way ships are not? This is Module 5’s cluster problem, live in your own answers.'
  },
  {
    id: 'makers-burden',
    name: 'The Maker’s Burden',
    requires: { 'meaning-source': 'made', 'eternal-recurrence': 'curse' },
    insight: 'You say meaning is made, not found, and you’d curse the demon’s offer of this exact life forever. Both answers are honest, and together they carry a job description: if meaning is made and the current draft isn’t one you’d sign eternally, the making is yours to do. The consultant’s epilogue in Module 8 is one picture of what that looks like.'
  },
  {
    id: 'settled-sceptic',
    name: 'The Settled Sceptic',
    requires: { 'awake-proof': 'cannot', 'vat-matters': 'nothing' },
    insight: 'You concede you can’t prove you’re awake, and you say the vat would change nothing, which means you’ve arrived, on your own steam, at philosophy’s working answer to radical doubt: irrefutable and idle. Calibrated confidence isn’t a compromise for you; it’s a position.'
  }
];

export const getPointsForPlacement = (moduleOrder, sectionIndex) =>
  POSITION_POINTS.filter(p => p.module === moduleOrder && p.section === sectionIndex);

export const getPointById = (id) => POSITION_POINTS.find(p => p.id === id);

export const evaluateTensions = (positions) =>
  POSITION_TENSIONS.filter(t =>
    Object.entries(t.requires).every(([forkId, optionId]) => positions[forkId]?.choice === optionId)
  );
